import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import OrdersAPI, { ExternalOrder, isExternalOrder, isInternalOrder, Order } from "../../api/OrdersAPI";
import { PaymentStatus } from "../../types/payment-statuses";
import { CancelToken } from "../../api/SimpleAPIClient";
import { Filters, useGetOrders } from "../../hooks/useGetOrders";
import { useGetShops } from "../../hooks/useGetShops";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import DropSelect from "../DropSelect/DropSelect";
import FilterPill from "../FilterPill/FilterPill";
import { Icon } from "../Icon/Icon";
import IconButton from "../IconButton/IconButton";
import { Modal } from "../Modal/Modal";
import OrderBasket from "../OrderBasket/OrderBasket";
import { containsFileType } from "../OrderElement/OrderElement";
import OrderForm from "../OrderForm/OrderForm";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import PrintButton from "../PrintButton/PrintButton";
import deleteIcon from "../../assets/images/bin.svg";
import info from "../../assets/images/info.svg";
import outIcon from "../../assets/images/arrow-up-right.svg";
import inIcon from "../../assets/images/arrow-down-right.svg";
import funnelIcon from "../../assets/images/funnel.svg";
import xMarkIcon from "../../assets/images/x-mark.svg";
import ConfirmationPopup from "../ConfirmationPopup/ConfirmationPopup";
import { ShopClient } from "../../api/ShopClientsAPI";
import { format } from "date-fns";
import { FormType } from "../../shared/form.type";
import Subtitle from "../Subtitle/Subtitle";
import { PermissionFlags } from "../PermissionFlags/PermissionFlags";
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor, paymentStatusToTranslationKey } from "../../shared/paymentStatusToTranslationKey";
import Pill from "../Pill/Pill";
import { BolderText } from "../BolderText/BolderText";
import useFeatureFlag from "../../hooks/usePermissionFlag";
import Card from "../Card/Card";
import { Link } from "../Link/Link";
import Table, { ColumnDefinition } from "../Table/Table";
import Tooltip from "../Tooltip/Tooltip";
import CONFIG from "../../config/config";
import NoData from "../NoData/NoData";
import ExternalOrderForm from "../ExternalOrderForm/ExternalOrderForm";
import { externalOrderStatusToColor, externalOrderStatusToTranslationKeys } from "../../shared/externalOrderStatusToTranslationKey";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, ExternalLink } from "lucide-react";
import SectionTitle from "../SectionTitle/SectionTitle";
import Switch from "../Switch/Switch";
import Tab from "../Tab/Tab";
import OrderSearchResult from "../OrderSearchResult/OrderSearchResult";
import { toastService } from "../../services/ToastService";
import { track } from "../../services/amplitude";
import SecondaryButton from "../SecondaryButton/SecondaryButton";

const DEFAULT_EXTERNAL_ORDERS_PAGE_SIZE = 100;
const EXTERNAL_ORDERS_PAGE_SIZE_OPTIONS = [50, 100, 200];

export type OrderColumn = ColumnDefinition<Order>;
export type ExternalOrderColumn = ColumnDefinition<ExternalOrder>;

function OrdersSegment() {

  const { t } = useTranslation();

  const { hasAccess: canPrioritize } = useFeatureFlag(['set-high-priority-orders']);
  const { hasAccess: canEdit } = useFeatureFlag(['update-orders', 'order-story-write']);
  const { hasAccess: canSetPendingMeasurements } = useFeatureFlag(['set-pending-measures-orders']);
  
  const { hasAccess: canSeeIncomingOrders } = useFeatureFlag(['see-incoming-orders']);
  const [filters, setFilters] = useState<Filters>({ shopId: '', shopIds: undefined, date: '', hidden: false, external: true, unresolved: false, statuses: undefined, paymentStatuses: undefined, page: 0, size: DEFAULT_EXTERNAL_ORDERS_PAGE_SIZE });
  const { isLoadingOrders, orders, externalOrders, externalOrdersPagination, updateFn, removeFn, refresh } = useGetOrders(filters);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [showBasket, setShowBasket] = useState(false);
  const [isNewOrder, setIsNewOrder] = useState(false);
  const [order, setOrder] = useState<Order | ExternalOrder | null>();
  const { shops } = useGetShops();
  const [action, setAction] = useState<FormType>('READ');
  const navigate = useNavigate();
  const location = useLocation();
  const [partnerSearchText, setPartnerSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const selectedStatuses = filters.statuses ?? [];
  const selectedPaymentStatuses = filters.paymentStatuses ?? [];
  const selectedShopIds = filters.shopIds ?? [];
  const isDeliveredTab = selectedStatuses.length === 1 && selectedStatuses[0] === 'DELIVERED';
  const isFiltering = !!filters.date || selectedShopIds.length > 0 || selectedPaymentStatuses.length > 0 || selectedStatuses.length > 0;
  const [isInitializing, setIsInitializing] = useState(true);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [pendingPartnersOrders, setPendingPartnersOrders] = useState<boolean | null>(null);

  const clearOrderQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("openForm");
    params.delete("orderId");
    params.delete("partnersOrders");
    navigate({ search: params.toString() }, { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    setExternal(canSeeIncomingOrders);
  }, [canSeeIncomingOrders])

  useEffect(() => {
    const isDataLoaded = !isLoadingOrders && canEdit !== undefined && canSeeIncomingOrders !== undefined;
    
    if (isDataLoaded && isInitializing) {
      setIsInitializing(false);
    }
  }, [isLoadingOrders, canEdit, canSeeIncomingOrders, isInitializing])

  useEffect(() => {
    return () => {
      setPendingOrderId(null);
      setPendingPartnersOrders(null);
    };
  }, [])

  useEffect(() => {
    if (!isInitializing && pendingOrderId) {
      if (pendingPartnersOrders) {
        OrdersAPI.getExternalOrderById(pendingOrderId)
          .then(response => {
            const foundOrder = response.data;
            if (foundOrder) {
              track('Order form opened', { source: 'existing' });
              if (canEdit) setAction("UPDATE");
              setOrder(foundOrder);
              setFilters(prev => ({ ...prev, external: true }));
            }
            clearOrderQueryParams();
            setPendingOrderId(null);
            setPendingPartnersOrders(null);
          })
          .catch(error => {
            console.error("Error fetching external order by ID:", error);
            clearOrderQueryParams();
            setPendingOrderId(null);
            setPendingPartnersOrders(null);
          });
      } else {
        OrdersAPI.getInternalOrderById(pendingOrderId)
          .then(response => {
            const foundOrder = response.data;
            if (foundOrder) {
              track('Order form opened', { source: 'existing' });
              if (canEdit) setAction("UPDATE");
              setOrder(foundOrder);
              setFilters(prev => ({ ...prev, external: false }));
            }
            clearOrderQueryParams();
            setPendingOrderId(null);
            setPendingPartnersOrders(null);
          })
          .catch(error => {
            console.error("Error fetching internal order by ID:", error);
            clearOrderQueryParams();
            setPendingOrderId(null);
            setPendingPartnersOrders(null);
          });
      }
    }
  }, [isInitializing, pendingOrderId, pendingPartnersOrders, canEdit, navigate, location.search, clearOrderQueryParams])

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openForm = params.get('openForm');
    const orderId = params.get("orderId");
    const partnersOrders = params.get("partnersOrders");

    if (openForm === 'true') {
      track('Order form opened', { source: 'create' });
      setShowBasket(true);
      setIsNewOrder(true);
      clearOrderQueryParams();
    }

    if (orderId) {
      setPendingOrderId(orderId);
      setPendingPartnersOrders(partnersOrders === 'true');
    }

  }, [location, navigate, clearOrderQueryParams]);

  const externalOrderColumns: ExternalOrderColumn[] = [
    {
      key: 'b2bType',
      title: "",
      render: (order: any) => {
        return order.b2bType === "OUT" ?
          <Tooltip position="right" content={t("orders.listing.external.outcoming")}><Icon url={outIcon} cssStyles="w-5"/></Tooltip>
          : <Tooltip position="right" content={t("orders.listing.external.incoming")}><Icon url={inIcon} cssStyles="w-5"/></Tooltip>
        },
    },
    {
      key: 'receiverShopName',
      title: t("orders.external.details.shop_name"),
      render: (order: any) =>
        order.b2bType === "OUT" ?
        <Tooltip content={order.receiverShopName}><p className="w-36 truncate">{order.receiverShopName}</p></Tooltip>
        : <Tooltip content={order.requesterShopName}><p className="w-36 truncate">{order.requesterShopName}</p></Tooltip>,
    },
    {
      key: 'clientName',
      title: t("orders.external.details.name"),
      render: (order: any) => <Tooltip content={order.clientName}><p className="w-36 truncate">{order.clientName}</p></Tooltip>,
    },
    {
        key: 'itemName',
        title: t("orders.external.details.item_name"),
        render: (order: any) => <p>{order.itemName}</p>,
    },
    {
      key: 'status',
      title: t("orders.external.details.status"),
      render: (order: any) =>
        (<>
        <Tooltip content={t(externalOrderStatusToTranslationKeys(order.status))}>
          <p className="w-12 sm:w-20 truncate">
            <Pill text={t(externalOrderStatusToTranslationKeys(order.status))} color={externalOrderStatusToColor(order.status)}/>
          </p>
        </Tooltip>
        { order.deletableFrom && 
          <Tooltip content={t("orders.external.details.status.deletable") + " " + order.deletableFrom}>
            <Icon url={info} cssStyles="w-3"/>
          </Tooltip>
        }
        </>),
    },
    {
      key: 'inStoreDate',
      title: t("orders.external.details.delivery_time"),
      render: (order: any) => <BolderText text={order.inStoreDate} /> ,
    },
    {
      key: 'creationTime',
      title: t("orders.external.details.submission_time"),
      render: (order: any) => {
        if (!order.creationTime) return <span>-</span>;
      
        let creationDate = new Date(parseFloat(order.creationTime) * 1000);
        if (isNaN(creationDate.getTime())) {
          return <span>-</span>;
        }

        const formattedDate = format(creationDate, "yyyy-MM-dd");
        return (
          <Tooltip content={formattedDate}>
            <p className="w-32 truncate">{formattedDate}</p>
          </Tooltip>
        );
      },
    },
    {
      key: 'paymentStatus',
      title: t("orders.details.payment_status"),
      render: (order: any) => (
        <Tooltip content={t(paymentStatusToTranslationKey(order.paymentStatus))}>
          <Pill text={t(paymentStatusToAbbreviatedTranslationKey(order.paymentStatus))} color={paymentStatusToPillColor(order.paymentStatus)}/>
        </Tooltip>
      ),
    },
    {
      key: 'attachmentUrls',
      title: t("orders.external.details.files"),
      render: (order: any) => (
        <div className="flex space-x-3">
          { order.notes? <span className="w-3">📝</span> : null }
          { containsFileType(order.attachmentUrls || [], CONFIG.VIDEO_TYPES)? <span className="w-3">📹</span> : null }
          { containsFileType(order.attachmentUrls || [], CONFIG.TEXT_TYPES.concat(CONFIG.IMAGE_TYPES))? <span className="w-3">📁</span> : null }
        </div>
      ),
    },
  ];

  const actionButton = (action: FormType) => {
    if (isLoadingOrders) {
      return <span className="flex justify-center"><PrimaryButton text="..." isLoading /></span>
    }
    switch (action) {
      case 'UPDATE':
        return <span className="flex justify-center"><PrimaryButton text={t("admin.collections.items.form.update")!} fn={processUpdateOrder} /></span>;
      default:
        return;
    }
  };

  const search = (input: string, resultsSize: number, setResultsFn: any) => {
    setSearchIsLoading(true);
    OrdersAPI.search(input).then((resp) => {
      const raw = resp.data;
      const respRaw = raw.slice(0, resultsSize);
      setSearchIsLoading(false);
      setResultsFn(respRaw);
    });
    return CancelToken.source().cancel("Cancelling...");
  };

  const processUpdateOrder = () => {
    if (isInternalOrder(order)) {
      processItem(updateFn, order);
    } else if (isExternalOrder(order)) {
      processItem(updateFn, order);
    }
  }

  async function processItem(actionFn: (item: Order | ExternalOrder) => Promise<void>, item: Order | ExternalOrder
  ) {
    await actionFn(item);
    setOrder(null);
    setAction('READ');
    refresh();
  }

  const view = (or: Order) => {
    track('Order form opened', { source: 'existing' });
    setOrder(or);
    if (canEdit) {
      setAction('UPDATE');
    } else {
      setAction('READ');
    }
  }

  const changePriority = async (or: Order) => {
    const existing = or.prioritized;
    const reverse = !existing;
    or.prioritized = reverse;
    processItem(updateFn, or);
  };

  const setPendingMeasurements = async (or: Order) => {
    const existing = or.pendingMeasures;
    const reverse = !existing;
    or.pendingMeasures = reverse;
    processItem(updateFn, or);
  };

  const hide = async (or: Order) => {
    or.visible = !or.visible;
    processItem(updateFn, or);
  };

  const toggleShop = (s: ShopClient) => {
    setFilters(prev => {
      const list = prev.shopIds ?? [];
      const id = String(s.id!);
      const next = list.includes(id) ? list.filter(x => x !== id) : [...list, id];
      return { ...prev, shopIds: next.length > 0 ? next : undefined, page: 0 };
    });
  }

  const setExternal = (status: boolean) => {
    setFilters(prev => {
        let copy = Object.assign({}, prev);
        return {...copy, external: status, status: ''};
    });
  }

  const setDate = (e: Date) => { 
    if (e) {
      const tempDate = new Date(e);
      const formatTempDate = format(tempDate!, "yyyy-MM-dd");
      setFilters(prev => ({ ...prev, date: formatTempDate, page: 0 }));
    } else {
      setFilters(prev => ({ ...prev, date: '', page: 0 }));
    }
  }

  const toggleStatus = (status: string) => {
    setFilters(prev => {
      const list = prev.statuses ?? [];
      const next = list.includes(status) ? list.filter(s => s !== status) : [...list, status];
      return { ...prev, statuses: next.length > 0 ? next : undefined, page: 0 };
    });
  };

  const togglePaymentStatus = (paymentStatus: PaymentStatus) => {
    setFilters(prev => {
      const list = prev.paymentStatuses ?? [];
      const next = list.includes(paymentStatus) ? list.filter(ps => ps !== paymentStatus) : [...list, paymentStatus];
      return { ...prev, paymentStatuses: next.length > 0 ? next : undefined, page: 0 };
    });
  };

  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(partnerSearchText.toLowerCase())
  );

  const isDifferentMonth = (date1: Date, date2: Date) => {
    return date1.getMonth() !== date2.getMonth() || date1.getFullYear() !== date2.getFullYear();
  };

  const createNewBatchOrder = () => {
    if (showBasket) {
      setShowBasket(prev => !prev)
    } else {
      track('Order form opened', { source: 'create' });
      setShowBasket(prev => !prev);
      setIsNewOrder(true);
    }
  }

  const closeModal = () => {
    setOrder(null);
    setAction('READ');
  }

  const handleAcceptAndCreateOrder = async (order: ExternalOrder) => {
    try {
      if (!order.id) {
        throw new Error("Order ID is required");
      }
      await OrdersAPI.updateExternal(order.id, {
        ...order,
        status: "ACCEPTED"
      });
      toastService.showToast(
        t("orders.external.actions.accept_and_create.success") || "Order accepted and production order created successfully",
        "success"
      );
      refresh();
    } catch (error) {
      console.error('Error accepting and creating order:', error);
      toastService.showToast(
        t("orders.external.actions.accept_and_create.error") || "Failed to accept and create order",
        "error"
      );
    }
  };

  const renderActionsOnExternal = (order: ExternalOrder, index: number): React.ReactNode => {
    const isIncoming = order.b2bType === "IN";
    const hasNoLinkedOrder = !order.toOrderId;
    const isNotAccepted = order.status === "DRAFT" || order.status === "WAITING" || order.status === "REQUIRES_CHANGES";
    const showAcceptButton = isIncoming && hasNoLinkedOrder && isNotAccepted;
    const hasLinkedProductionOrder = isIncoming && order.toOrderId;

    return (
      <span className="flex space-x-3">
        {showAcceptButton && (
          <PermissionFlags requiredFlags={['update-orders'] || []}>
          <IconButton
            actionName="Accept and Create Order in Production"
            onClick={(e) => {
              e.stopPropagation();
              handleAcceptAndCreateOrder(order);
            }}
          >
            <Tooltip content={t("orders.external.actions.accept_and_create") || "Accept and create order in production"}>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        )}
        {hasLinkedProductionOrder && (
          <PermissionFlags requiredFlags={['see-orders'] || []}>
            <IconButton
              actionName="View Production Order"
              onClick={(e) => {
                window.open(`/production-orders?orderId=${order.toOrderId}`, '_blank');
              }}
            >
              <Tooltip content={t("orders.external.actions.view_production_order") || "View production order"}>
                <ExternalLink className="w-5 h-5 text-blue-600" />
              </Tooltip>
            </IconButton>
          </PermissionFlags>
        )}
        <PermissionFlags requiredFlags={['remove-orders'] || []}>
          <IconButton
            actionName="Edit External Order"
            >
            <ConfirmationPopup onConfirm={() => removeFn(order)} confirmText={"orders.listing.external.remove.warning"} title={`${t("global.remove")}`}>
              <Icon url={deleteIcon} cssStyles="w-5"/>
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </span>
    );
  };

  return (
      <div className="mt-5 space-y-5">
      <Card>
        <>
          <div className="flex-col">
            <div className="md:flex md:flex-row md:items-center md:space-x-3">
              <div className="flex items-center space-x-3 mb-2 md:mb-0">
                <DropSelect compact onSelect={(e: any) => setDate(e.target.value)} value={filters?.date?.toString()}/>
                <span className="md:hidden">
                  <SecondaryButton 
                    text={<span className="inline-flex items-center">{t("filters.button") || "Filters"}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                    fn={() => setShowFilters(!showFilters)}
                    icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                  />
                </span>
              </div>
              <Autocomplete
                resultsSize={15}
                searchFn={search}
                onSelect={() => null}
                placeholder={"orders.search.placeholder"} 
                isLoading={searchIsLoading}
                errorMessage={"global.empty_results"}
                toElementFn={(or: Order) => 
                  <OrderSearchResult
                    key={or.id}
                    order={or}
                    onView={view}
                    onSetPendingMeasurements={canSetPendingMeasurements ? setPendingMeasurements : undefined}
                    onChangePriority={canPrioritize ? changePriority : undefined}
                    onHide={hide}
                    canSetPendingMeasurements={canSetPendingMeasurements}
                    canPrioritize={canPrioritize}
                  />
                }
              />
              <div className="hidden md:flex">
                <SecondaryButton 
                  text={<span className="inline-flex items-center">{t("filters.button") || "Filters"}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                  fn={() => setShowFilters(!showFilters)}
                  icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                />
              </div>
            </div>
          </div>
          {showFilters && (
            <>
              <div className="space-y-2 mt-2">
                {/* Partner Shops Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t("orders.filters.shops.title") || "Partner Shops"}</h3>
                  <div className="w-full max-w-full">
                    <label>
                      <span className="autocompleteInputWrapper formValue autocomplete">
                        <input
                          className="p-3 w-full border-stone-200 border rounded focus:outline-none"
                          type="text"
                          placeholder={t("orders.filters.shops.search_placeholder")!}
                          value={partnerSearchText}
                          onChange={(e) => setPartnerSearchText(e.target.value)}
                          data-dd-action-name={'Partner Search'}
                        />
                      </span>
                    </label>
                  </div>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {
                      filteredShops.length > 0 ? filteredShops.map((s, i) => <FilterPill isActive={selectedShopIds.includes(String(s.id))} key={i} text={s.name} fn={() => toggleShop(s)} />)
                      : partnerSearchText ? (
                        <p>{t("orders.filters.shops.no_results")}</p>
                      ) : (
                        <>
                          <p>{t("orders.filters.shops.empty")}.</p>&nbsp;
                          <Link url="/partners" text={t("orders.filters.shops.empty.cta")}/>
                        </>
                      )
                    }
                  </div>
                </div>

                {/* Order Status Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t("orders.filters.status.title") || "Order Status"}</h3>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {CONFIG.EXTERNAL_ORDERS.STATUS
                      .filter(status => status !== 'DELIVERED')
                      .map(status => ({
                        status,
                        translatedText: t(externalOrderStatusToTranslationKeys(status))
                      }))
                      .sort((a, b) => a.translatedText.localeCompare(b.translatedText))
                      .map(({ status, translatedText }, i) => (
                        <FilterPill
                          key={i}
                          text={translatedText}
                          isActive={selectedStatuses.includes(status)}
                          fn={() => toggleStatus(status)}
                        />
                      ))}
                  </div>
                </div>

                {/* Payment Status Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t("orders.filters.payment_status.title") || "Payment Status"}</h3>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {CONFIG.ORDERS.PAYMENT_STATUS.map((paymentStatus, i) => (
                      <FilterPill 
                        key={i} 
                        text={t(paymentStatusToTranslationKey(paymentStatus))} 
                        isActive={selectedPaymentStatuses.includes(paymentStatus)}
                        fn={() => togglePaymentStatus(paymentStatus)} 
                      />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex-col md:flex md:flex-row justify-between md:justify-start items-center space-x-4 mt-3">
            <PermissionFlags requiredFlags={['order-add'] || []}>
              <PrimaryButton actionName="Open/Close Order Basket" text={showBasket ? t("orders.orders_basket.close_form")! : t("orders.orders_basket.add_form")!} fn={() => createNewBatchOrder()}/>
            </PermissionFlags>
            <PermissionFlags requiredFlags={['print-orders'] || []}>
              <PrintButton orders={orders}/>
            </PermissionFlags>
            { isFiltering && (
              <SecondaryButton 
                actionName="Show All Orders" 
                text={t("filters.reset")!} 
                fn={() => setFilters(prev => ({ ...prev, shopId: '', shopIds: undefined, date: '', paymentStatuses: undefined, statuses: undefined, page: 0 }))}
                icon={
                  <span className="relative inline-block w-4 h-4">
                    <Icon url={funnelIcon} cssStyles="w-4 h-4" />
                    <span className="absolute -top-1 -right-1">
                      <Icon url={xMarkIcon} cssStyles="w-3 h-3" />
                    </span>
                  </span>
                }
                compact
              />
            ) }
          </div>
        </>
      </Card>
        { showBasket && <><OrderBasket openFormOnLoad={isNewOrder} fn={refresh} isExternal={location.pathname === '/orders'} /></> }
        <Card>
          <>
            <div className="flex">
              <Tab
                fn={() => setFilters(prev => ({ ...prev, statuses: undefined }))}
                label={t("orders.listing.external.tab_all")}
                isActive={!isDeliveredTab}
              />
              <Tab
                fn={() => setFilters(prev => ({ ...prev, statuses: ['DELIVERED'] }))}
                label={t("orders.external.status.delivered")}
                isActive={isDeliveredTab}
              />
            </div>
            <>
              <div className="container">
                <div className="flex justify-end mb-4">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="unresolved-filter" className="text-sm font-medium text-gray-700">
                      {t("orders.listing.external.unresolved")}
                    </label>
                    <Switch
                      checked={filters.unresolved || false}
                      onChange={() => setFilters(prev => ({ ...prev, unresolved: !prev.unresolved }))}
                    />
                  </div>
                </div>
              <Table<ExternalOrder>
                  isLoading={isLoadingOrders}
                  data={externalOrders}
                  columns={externalOrderColumns}
                  onRowClick={(or) => { track('Order form opened', { source: 'existing' }); setOrder(or); setAction(canEdit ? 'UPDATE' : 'READ'); }}
                  emptyListComponent={<NoData message={t("orders.empty.cta.title")} />}
                  selectedItems={[]}
                  isSelectable={false}
                  compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                  dividerCondition={(current, next) => isDifferentMonth(new Date(current.inStoreDate), new Date(next.inStoreDate))}
                  renderDivider={(current) => (
                    <div className="mt-10 mb-4 flex items-end space-x-2">
                      <Subtitle text={`${t(`global.${format(new Date(current.inStoreDate), "MMMM").toLowerCase()}`)}`} />
                      <SectionTitle text={`${format(new Date(current.inStoreDate), "yyyy")}`} />
                    </div>
                  )}
                  renderActions={renderActionsOnExternal}
              />
              {externalOrdersPagination != null && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4 px-4 pb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {t("global.pagination.show", "Show")}:
                    </span>
                    <select
                      id="external-orders-page-size"
                      value={filters.size ?? DEFAULT_EXTERNAL_ORDERS_PAGE_SIZE}
                      onChange={(e) => setFilters(prev => ({ ...prev, size: Number(e.target.value), page: 0 }))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {EXTERNAL_ORDERS_PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-600">
                      {t("global.pagination.per_page", "per page")}
                    </span>
                  </div>
                  {externalOrdersPagination.totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <SecondaryButton
                        text={t("global.previous", "Prev") as string}
                        fn={() => setFilters(prev => ({ ...prev, page: Math.max(0, (prev.page ?? 0) - 1) }))}
                        isDisabled={externalOrdersPagination.number === 0}
                        actionName="External orders pagination prev"
                      />
                      <span className="px-3 py-1 text-sm text-gray-600">
                        {t("global.pagination.page", "Page {{page}} of {{totalPages}}", {
                          page: externalOrdersPagination.number + 1,
                          totalPages: externalOrdersPagination.totalPages,
                        })}
                      </span>
                      <SecondaryButton
                        text={t("global.next", "Next") as string}
                        fn={() => setFilters(prev => ({ ...prev, page: (prev.page ?? 0) + 1 }))}
                        isDisabled={externalOrdersPagination.number >= externalOrdersPagination.totalPages - 1}
                        actionName="External orders pagination next"
                      />
                    </div>
                  )}
                </div>
              )}
              </div>
            </>
          </>
        </Card>
        { order && 
          <Modal modalTitle={order.clientName} canClose onClose={closeModal}>
            { location.pathname === '/production-orders' ?
              <OrderForm
                order={isInternalOrder(order) ? order as Order : null}
                isDisabled={action === 'READ' || isLoadingOrders}
                onOrderChange={setOrder}
                actionButton={actionButton(action)}
              />
              : <ExternalOrderForm
                order={order as ExternalOrder}
                isDisabled={action === 'READ' || isLoadingOrders}
                onOrderChange={setOrder}
                actionButton={actionButton(action)}
              />
            }
          </Modal>
        }
      </div>
  );
}

export default OrdersSegment;
